import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View,
  Platform,
  Text as RNText,
  TouchableOpacity,
  type GestureResponderEvent,
  type TextStyle,
} from 'react-native';
import { Text, ITextProps } from '@/components/gluestack/text'; // Import ITextProps
import { Link } from 'expo-router';
import { decodeHTML } from 'entities';
import { WebBrowserPresentationStyle } from 'expo-web-browser';
import { useOpenWebPage } from '~/hooks/useOpenWebPage';
import { useTranslation } from 'react-i18next';
import { useRecyclingState } from '@shopify/flash-list';
import { Box } from '@/components/gluestack/box';
import { YouTubePlayer } from '@/components/ui/YouTubePlayer';
import { useAppLink } from '@/components/providers/AppLinkProvider';
import { getHashtagHref } from '@/constants/routes';

interface RenderHtmlProps {
  /** The HTML string to parse and render */
  html: string;
  /** CSS classes to apply to text elements */
  className?: string;
  /** Maximum number of characters before truncation (default: 600) */
  characterLimit?: number;
  /** Text size from Gluestack UI Text component */
  size?: ITextProps['size'];
  /** Whether to disable content truncation entirely */
  noTruncate?: boolean;
  /** Whether to remove line breaks and put all text on a single line */
  removeLineBreaks?: boolean;
  /** Whether to detect and make HTTPS URLs clickable links */
  detectHttpsLinks?: boolean;
  /** Whether to make hashtags clickable links to the posts using them */
  detectHashtags?: boolean;
  /** CSS classes to apply to link elements (default: 'text-primary-500 underline') */
  linkClassName?: string;
  /** Whether to detect and embed YouTube videos from URLs */
  showYouTube?: boolean;
  /** CSS classes to apply to media containers (like YouTube player) */
  mediaClassName?: string;
  /** Whether to hide the "Read More" button when content is truncated */
  noReadMoreButton?: boolean;
  /** Callback fired when truncation state is determined */
  onTruncationChange?: (isTruncated: boolean) => void;
  /** Passed to Gluestack `Text` nodes (Android bold-text layout workaround). Default `1` on `Text`. */
  androidBoldTextTrailingSpaces?: ITextProps['androidBoldTextTrailingSpaces'];
}

const URL_REGEX = /(https:\/\/[^\s<>"{}|\\^`[\]]+)/g;
// Same rule as the legacy website: '#' at the start or after whitespace, and the tag contains a letter
const HASHTAG_REGEX = /(^|\s)#([\p{L}\p{M}\p{N}_]*\p{L}[\p{L}\p{M}\p{N}_]*)/gu;
const HASHTAG_CLASS_NAME = 'text-primary-500 no-underline web:hover:underline';
// On web the surrounding Text is a plain <span>, so nested react-native-web Text (links, hashtags) would fall back to its default 14px font
const INHERIT_FONT_STYLE =
  Platform.OS === 'web'
    ? ({
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontStyle: 'inherit',
        fontWeight: 'inherit',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',
      } as unknown as TextStyle)
    : undefined;
// Keeps a single-word link (URL, hashtag) on one line when it fits; it only breaks when wider than the text
const UNBROKEN_LINK_STYLE =
  Platform.OS === 'web'
    ? ({ ...INHERIT_FONT_STYLE, display: 'inline-block', maxWidth: '100%' } as unknown as TextStyle)
    : undefined;
const YOUTUBE_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

const isExternalUrl = (url: string) => url.startsWith('http://') || url.startsWith('https://');

// Plain text gets its own element: a bare string beside links is a separate DOM text node, which browser page translation replaces, so React's later removeChild throws
const createPlainText = (text: string, key: string) =>
  Platform.OS === 'web' ? <span key={key}>{text}</span> : <RNText key={key}>{text}</RNText>;

const stopPressPropagation = (event: GestureResponderEvent | { stopPropagation?: () => void }) => {
  event.stopPropagation?.();
};

const extractYouTubeVideoId = (url: string): string | null => {
  const match = url.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
};

const findYouTubeVideoId = (
  htmlContent: string,
  showYouTube: boolean,
  detectHttpsLinks: boolean
): string | null => {
  if (!showYouTube || !detectHttpsLinks) return null;

  URL_REGEX.lastIndex = 0;
  let urlMatch;
  while ((urlMatch = URL_REGEX.exec(htmlContent)) !== null) {
    const url = urlMatch[1];
    const videoId = extractYouTubeVideoId(url);
    if (videoId) return videoId;
  }

  return null;
};

const removeFirstYouTubeLink = (
  htmlContent: string,
  videoId: string,
  detectHttpsLinks: boolean
): string => {
  if (!videoId || !detectHttpsLinks) return htmlContent;

  URL_REGEX.lastIndex = 0;
  let urlMatch;
  while ((urlMatch = URL_REGEX.exec(htmlContent)) !== null) {
    const url = urlMatch[1];
    if (extractYouTubeVideoId(url) === videoId) {
      return htmlContent.replace(url, '');
    }
  }

  return htmlContent;
};

const truncateUrlDisplay = (urlString: string, extraChars: number = 35): string => {
  try {
    const urlObj = new URL(urlString);
    const origin = `${urlObj.protocol}//${urlObj.host}`;
    let rest = urlObj.href.substring(origin.length);
    const qIndex = rest.indexOf('?');
    const hIndex = rest.indexOf('#');
    const cutIndex = [qIndex, hIndex].filter((i) => i >= 0).sort((a, b) => a - b)[0];
    if (cutIndex !== undefined) {
      rest = rest.slice(0, cutIndex);
    }
    const extra = rest.slice(0, extraChars);
    const wasTruncated = rest.length > extraChars;
    return origin + extra + (wasTruncated ? '...' : '');
  } catch {
    const match = urlString.match(/^(https?:\/\/[^/]+)(.*)$/);
    if (match) {
      const origin = match[1];
      let rest = match[2] || '';
      const qIndex = rest.indexOf('?');
      const hIndex = rest.indexOf('#');
      const cutIndex = [qIndex, hIndex].filter((i) => i >= 0).sort((a, b) => a - b)[0];
      if (cutIndex !== undefined) {
        rest = rest.slice(0, cutIndex);
      }
      const extra = rest.slice(0, extraChars);
      const wasTruncated = rest.length > extraChars;
      return origin + extra + (wasTruncated ? '...' : '');
    }

    return urlString.length > extraChars ? urlString.slice(0, extraChars) + '...' : urlString;
  }
};

function RenderHtmlComponent({
  html,
  className = '',
  characterLimit = 600,
  size = 'md',
  noTruncate = false,
  removeLineBreaks = false, // puts all text on one line
  detectHttpsLinks = false,
  detectHashtags = false,
  linkClassName = 'text-primary-500 underline',
  showYouTube = false,
  mediaClassName,
  noReadMoreButton = false,
  onTruncationChange,
  androidBoldTextTrailingSpaces,
}: RenderHtmlProps) {
  const { t } = useTranslation();
  const { isRoutableAppLink, openAppLink } = useAppLink();
  const openWebPage = useOpenWebPage();
  const [showFullContent, setShowFullContent] = useRecyclingState(noTruncate, [html, noTruncate]);
  const currentTruncationState = useRef<boolean>(false);

  // Call truncation callback after render when state changes
  useEffect(() => {
    if (onTruncationChange) {
      onTruncationChange(currentTruncationState.current);
    }
  }, [onTruncationChange, showFullContent, noTruncate, characterLimit, html]);

  const decodedHtml = useMemo(() => {
    let nextHtml = decodeHTML(html);

    if (removeLineBreaks) {
      nextHtml = nextHtml
        .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
        .replace(/<\/?p[^>]*>/gi, ' ')
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/\r?\n/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    return nextHtml;
  }, [html, removeLineBreaks]);

  const youTubeVideoId = useMemo(
    () => findYouTubeVideoId(decodedHtml, showYouTube, detectHttpsLinks),
    [decodedHtml, showYouTube, detectHttpsLinks]
  );

  const htmlWithoutEmbeddedYouTube = useMemo(
    () =>
      youTubeVideoId
        ? removeFirstYouTubeLink(decodedHtml, youTubeVideoId, detectHttpsLinks)
        : decodedHtml,
    [decodedHtml, youTubeVideoId, detectHttpsLinks]
  );

  const handleAppLinkPress = useCallback(
    (event: GestureResponderEvent, url: string) => {
      stopPressPropagation(event);
      void openAppLink(url, { source: 'internal', navigationAction: 'push' });
    },
    [openAppLink]
  );

  const handleExternalLinkPress = useCallback(
    (event: GestureResponderEvent, url: string) => {
      stopPressPropagation(event);
      void openWebPage(url, false, WebBrowserPresentationStyle.FULL_SCREEN);
    },
    [openWebPage]
  );

  // Links are nested in the paragraph's Text so they flow with the surrounding text
  const createLinkElement = useCallback(
    (href: string, linkText: string, key: string) => {
      const isExternal = isExternalUrl(href);
      const isSupportedAppLink = isExternal && isRoutableAppLink(href);
      const linkTextClassName = `${linkClassName} break-all`;
      const linkStyle = /\s/.test(linkText) ? INHERIT_FONT_STYLE : UNBROKEN_LINK_STYLE;

      // Web: a real <a> for every link (focusable, middle-click); community links open in the app
      if (Platform.OS === 'web') {
        return (
          <Link
            key={key}
            href={href as any}
            className={linkTextClassName}
            style={linkStyle}
            onPress={(event) => {
              if (isSupportedAppLink) {
                event.preventDefault();
                handleAppLinkPress(event as GestureResponderEvent, href);
              } else {
                stopPressPropagation(event);
              }
            }}
            {...(isSupportedAppLink ? {} : { target: '_blank', rel: 'noopener noreferrer' })}>
            {linkText}
          </Link>
        );
      }

      if (isExternal) {
        return (
          <RNText
            key={key}
            className={linkTextClassName}
            onPress={(event) =>
              isSupportedAppLink
                ? handleAppLinkPress(event, href)
                : handleExternalLinkPress(event, href)
            }>
            {linkText}
          </RNText>
        );
      }

      // asChild: Link's own className is dropped on native, so style the nested Text
      return (
        <Link key={key} href={href as any} asChild onPress={stopPressPropagation}>
          <RNText className={linkTextClassName}>{linkText}</RNText>
        </Link>
      );
    },
    [handleAppLinkPress, handleExternalLinkPress, isRoutableAppLink, linkClassName]
  );

  const createInlineText = useCallback(
    (content: string, key: string) => {
      const children: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      HASHTAG_REGEX.lastIndex = 0;
      while (detectHashtags && (match = HASHTAG_REGEX.exec(content)) !== null) {
        const hashtagIndex = match.index + match[1].length;
        const hashtag = `#${match[2]}`;
        if (hashtagIndex > lastIndex) {
          children.push(
            createPlainText(content.substring(lastIndex, hashtagIndex), `${key}-plain-${lastIndex}`)
          );
        }
        children.push(
          <Link
            key={`${key}-hashtag-${hashtagIndex}`}
            href={getHashtagHref(hashtag)}
            push
            asChild
            onPress={stopPressPropagation}>
            {/* asChild: Link's own className is dropped on native, so style the nested Text */}
            <RNText className={HASHTAG_CLASS_NAME} style={UNBROKEN_LINK_STYLE}>
              {hashtag}
            </RNText>
          </Link>
        );
        lastIndex = hashtagIndex + hashtag.length;
      }

      if (lastIndex < content.length) {
        children.push(createPlainText(content.substring(lastIndex), `${key}-plain-${lastIndex}`));
      }

      return children;
    },
    [detectHashtags]
  );

  // Function to create text element
  const createTextElement = useCallback(
    (content: string, key: string) => {
      // Only detect HTTPS links if the prop is enabled
      if (!detectHttpsLinks) {
        return createInlineText(content, key);
      }

      // Check if content contains URLs that start with https://
      // Robust regex that matches valid URLs with proper domain structure
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;

      URL_REGEX.lastIndex = 0;
      while ((match = URL_REGEX.exec(content)) !== null) {
        // Process text before the URL
        if (match.index > lastIndex) {
          const textContent = content.substring(lastIndex, match.index);
          if (textContent) {
            parts.push(...createInlineText(textContent, `${key}-text-${lastIndex}`));
          }
        }

        // Process the URL
        const url = match[1];
        const displayUrl = truncateUrlDisplay(url);
        parts.push(createLinkElement(url, displayUrl, `${key}-url-${match.index}`));

        lastIndex = match.index + match[0].length;
      }

      // Process remaining text after last URL
      if (lastIndex < content.length) {
        const textContent = content.substring(lastIndex);
        if (textContent) {
          parts.push(...createInlineText(textContent, `${key}-text-${lastIndex}`));
        }
      }

      return parts;
    },
    [createInlineText, createLinkElement, detectHttpsLinks]
  );

  // Simple parser for basic HTML tags with truncation support
  const parsedContent = useMemo(() => {
    // First, normalize consecutive <br/> tags, \r\n, and \n sequences to single separators
    const normalizedHtml = htmlWithoutEmbeddedYouTube
      // Replace multiple consecutive \r\n sequences with a single separator
      .replace(/(\r\n(\s*\r\n)+)/g, '||PARAGRAPH_BREAK||')
      // Replace single \r\n with separator
      .replace(/\r\n/g, '||PARAGRAPH_BREAK||')
      // Replace multiple consecutive \n sequences with a single separator
      .replace(/(\n(\s*\n)+)/g, '||PARAGRAPH_BREAK||')
      // Replace single \n with separator
      .replace(/\n/g, '||PARAGRAPH_BREAK||')
      // Replace multiple consecutive <br/> tags with a single separator
      .replace(/(<br\/>(\s*<br\/>)+)/g, '||PARAGRAPH_BREAK||')
      // Replace single <br/> tags with separator
      .replace(/<br\/>/g, '||PARAGRAPH_BREAK||');

    // Split by both paragraph tags and normalized br tags
    const paragraphs = normalizedHtml
      .split(/<\/?p[^>]*>|(\|\|PARAGRAPH_BREAK\|\|)/g)
      .filter(Boolean)
      .filter((part) => part !== '||PARAGRAPH_BREAK||')
      .map((part) => part.trim())
      .filter(Boolean);

    // One Text per paragraph so links and hashtags flow inline with the surrounding text
    const createParagraph = (parts: React.ReactNode[], pIndex: number) => (
      <View key={`paragraph-${pIndex}`} className="my-1">
        <Text
          size={size}
          className={`${className} break-words`}
          androidBoldTextTrailingSpaces={androidBoldTextTrailingSpaces}>
          {parts}
        </Text>
      </View>
    );

    let totalTextLength = 0;
    let shouldTruncate = false;
    const result = [];

    // Stop processing if we should truncate and not showing full content
    for (
      let pIndex = 0;
      pIndex < paragraphs.length && (!shouldTruncate || showFullContent);
      pIndex++
    ) {
      // Account for paragraph change cost if not the first paragraph and truncation is active
      if (pIndex > 0 && !noTruncate && !showFullContent) {
        totalTextLength += 45; // Cost for paragraph change

        // If this cost itself pushes us over the limit, mark for truncation and stop
        if (totalTextLength >= characterLimit) {
          shouldTruncate = true;
          break; // Stop processing more paragraphs
        }
      }

      const paragraph = paragraphs[pIndex];
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let paragraphTextLength = 0;

      // Regular expression to find <a> tags
      const linkRegex = /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/g;
      let match;

      while ((match = linkRegex.exec(paragraph)) !== null) {
        // Process text before the link
        if (match.index > lastIndex) {
          const textContent = paragraph.substring(lastIndex, match.index);

          // Check if adding this text would exceed limit
          if (
            !noTruncate &&
            !showFullContent &&
            totalTextLength + paragraphTextLength + textContent.length > characterLimit
          ) {
            // Need to truncate here
            const remainingChars = characterLimit - totalTextLength - paragraphTextLength;
            let truncateAt = remainingChars;

            // Find word boundary
            while (
              truncateAt > 0 &&
              textContent[truncateAt] !== ' ' &&
              textContent[truncateAt] !== '.' &&
              textContent[truncateAt] !== ','
            ) {
              truncateAt--;
            }

            // If no good break point found, use the character limit
            truncateAt = truncateAt <= 0 ? remainingChars : truncateAt + 1;

            const truncatedText = textContent.substring(0, truncateAt) + '...';
            const textElements = createTextElement(truncatedText, `text-${pIndex}-${lastIndex}`);
            parts.push(...textElements);

            shouldTruncate = true;
            break;
          } else {
            const textElements = createTextElement(textContent, `text-${pIndex}-${lastIndex}`);
            parts.push(...textElements);
            paragraphTextLength += textContent.length;
          }
        }

        // If we need to truncate, stop before processing the link
        if (shouldTruncate) break;

        // Process the link
        const href = match[2];
        const linkText = match[3].replace(/<\/?[^>]+(>|$)/g, ''); // Remove any nested tags
        const displayText = isExternalUrl(linkText) ? truncateUrlDisplay(linkText) : linkText;

        parts.push(createLinkElement(href, displayText, `link-${pIndex}-${match.index}`));

        lastIndex = match.index + match[0].length;
      }

      // If we need to truncate, don't process any more text
      if (shouldTruncate) {
        result.push(createParagraph(parts, pIndex));
        break;
      }

      // Process remaining text after last link (or all text if no links)
      if (lastIndex < paragraph.length) {
        const textContent = paragraph.substring(lastIndex);

        // Check if adding this text would exceed limit
        if (
          !noTruncate &&
          !showFullContent &&
          totalTextLength + paragraphTextLength + textContent.length > characterLimit
        ) {
          // Need to truncate here
          const remainingChars = characterLimit - totalTextLength - paragraphTextLength;
          let truncateAt = remainingChars;

          // Find word boundary
          while (
            truncateAt > 0 &&
            textContent[truncateAt] !== ' ' &&
            textContent[truncateAt] !== '.' &&
            textContent[truncateAt] !== ','
          ) {
            truncateAt--;
          }

          // If no good break point found, use the character limit
          truncateAt = truncateAt <= 0 ? remainingChars : truncateAt + 1;

          const truncatedText = textContent.substring(0, truncateAt) + '...';
          const textElements = createTextElement(truncatedText, `text-${pIndex}-${lastIndex}`);
          parts.push(...textElements);

          shouldTruncate = true;
        } else {
          const textElements = createTextElement(textContent, `text-${pIndex}-${lastIndex}`);
          parts.push(...textElements);
          paragraphTextLength += textContent.length;
        }
      }

      // If no content was added to parts but paragraph exists, add it as plain text
      if (parts.length === 0 && paragraph) {
        if (
          !noTruncate &&
          !showFullContent &&
          totalTextLength + paragraph.length > characterLimit
        ) {
          // Need to truncate here
          const remainingChars = characterLimit - totalTextLength;
          let truncateAt = remainingChars;

          // Find word boundary
          while (
            truncateAt > 0 &&
            paragraph[truncateAt] !== ' ' &&
            paragraph[truncateAt] !== '.' &&
            paragraph[truncateAt] !== ','
          ) {
            truncateAt--;
          }

          // If no good break point found, use the character limit
          truncateAt = truncateAt <= 0 ? remainingChars : truncateAt + 1;

          const truncatedText = paragraph.substring(0, truncateAt) + '...';
          const textElements = createTextElement(truncatedText, `text-${pIndex}`);
          parts.push(...textElements);

          shouldTruncate = true;
        } else {
          const textElements = createTextElement(paragraph, `text-${pIndex}`);
          parts.push(...textElements);
          paragraphTextLength += paragraph.length;
        }
      }

      // Add the paragraph view to results
      result.push(createParagraph(parts, pIndex));

      // Update total text length for next paragraph
      totalTextLength += paragraphTextLength;

      // Stop if we should truncate
      if (shouldTruncate) break;
    }

    // Determine if content is actually truncated and store in ref
    const isTruncated = shouldTruncate && !showFullContent && !noTruncate;
    currentTruncationState.current = isTruncated;

    // Add "Read More" button if content was truncated
    if (isTruncated && !noReadMoreButton) {
      result.push(
        <TouchableOpacity
          key="read-more-button"
          onPress={(event) => {
            stopPressPropagation(event);
            setShowFullContent(true);
          }}
          className="my-1 self-start">
          <Text size={size} className={`${className} ${linkClassName} break-words font-medium`}>
            {t('post.readMore')}
          </Text>
        </TouchableOpacity>
      );
    }

    // Add YouTube player if video ID is found
    if (youTubeVideoId) {
      result.push(
        <Box key="youtube-player" className={mediaClassName}>
          <YouTubePlayer
            videoId={youTubeVideoId}
            useAspectRatio
            className="mx-auto w-full"
            unloadOnInvisible
          />
        </Box>
      );
    }

    return result;
  }, [
    androidBoldTextTrailingSpaces,
    characterLimit,
    className,
    createLinkElement,
    createTextElement,
    htmlWithoutEmbeddedYouTube,
    linkClassName,
    mediaClassName,
    noReadMoreButton,
    noTruncate,
    showFullContent,
    size,
    t,
    youTubeVideoId,
  ]);

  // Return empty View if no HTML
  if (!html) {
    return null;
  }

  return parsedContent;
}

export const RenderHtml = React.memo(RenderHtmlComponent);
